import {
  type BrandProfilesListQuery,
  useBrandProfilesListQuery,
} from "src/graphql";

type BrandProfileRow = BrandProfilesListQuery["brand_profiles"][number];

/** Just the fields the brand-profile pickers render; logos only where a picker shows them. */
export type BrandProfileOption = Pick<BrandProfileRow, "id" | "name"> &
  Partial<Pick<BrandProfileRow, "logos">>;

// Declared outside the hook so React Query's `select` reference stays stable.
const selectBrandProfileOptions = (
  data: BrandProfilesListQuery,
): BrandProfileOption[] => data.brand_profiles;

/**
 * The workspace's brand profiles, and whether there is a choice worth offering.
 *
 * Shared by the chat composer pill and the report content form so both surfaces
 * agree on when a brand-profile control exists at all — and so a workspace that
 * is never offered the choice can never end up with a brand id persisted.
 */
export const useBrandProfileOptions = () => {
  const { data: brandProfiles, isLoading } = useBrandProfilesListQuery(
    undefined,
    {
      select: selectBrandProfileOptions,
    },
  );

  // Undefined until the list resolves, and forever after it fails — react-query
  // runs with `retry: false`, so a failed list never arrives. Both windows land
  // on `false`, which hides the control. That is coherent rather than a gap: a
  // hidden control and an absent id both mean "All profiles", so what a caller
  // shows can never disagree with what submitting in that instant does.
  const hasBrandProfileChoice = (brandProfiles?.length ?? 0) > 1;

  return {
    /** Undefined until the profiles query resolves, and after it fails. */
    brandProfiles,
    /** Whether to render a picker: false when there's no brand to choose between. */
    hasBrandProfileChoice,
    /** True until the profiles query first settles, whether it succeeds or fails. */
    isLoading,
  };
};
